import { Clock, Flag, GitBranch, Hourglass, Mail, Play, Tag, Zap, type LucideIcon } from 'lucide-react';
import type { StepKind } from '../../../types/automazioni.types';

/**
 * Il colore di ogni tipo di casella, uguale ovunque: sul tasto che la crea,
 * sulla casella nella lavagna, nel pannello e nella mappa in miniatura. Chi
 * guarda il percorso riconosce il tipo dal colore prima ancora di leggere.
 *
 * Il rosso non e' fra i colori: sulla lavagna vuol dire "manca qualcosa".
 * Niente azzurri: sono i colori dell'azienda da cui Pixel e' stato copiato
 * (scripts/refactor-checks.sh li cerca).
 */
export interface AspettoPasso {
  icona: LucideIcon;
  /** Il riquadro pieno: tasto per aggiungere e icona della casella. */
  pieno: string;
  /** Il tasto quando ci passi sopra. */
  pienoHover: string;
  /** Testo colorato su fondo bianco. */
  testo: string;
  /** Fondo chiaro, per le etichette. */
  tenue: string;
  /** Il bordo della casella scelta. */
  anello: string;
  /** Lo stesso colore in esadecimale: la mappa e le frecce non leggono Tailwind. */
  esadecimale: string;
}

export const ASPETTO_PASSI: Record<StepKind | 'inizio', AspettoPasso> = {
  inizio: {
    icona: Play,
    pieno: 'bg-neutral-900 text-white',
    pienoHover: 'hover:bg-neutral-800',
    testo: 'text-neutral-900',
    tenue: 'bg-neutral-100 text-neutral-700',
    anello: 'ring-neutral-900',
    esadecimale: '#171717',
  },
  invio: {
    icona: Mail,
    pieno: 'bg-stone-700 text-white',
    pienoHover: 'hover:bg-stone-800',
    testo: 'text-stone-700',
    tenue: 'bg-stone-100 text-stone-700',
    anello: 'ring-stone-500',
    esadecimale: '#44403c',
  },
  attesa: {
    icona: Clock,
    pieno: 'bg-amber-500 text-white',
    pienoHover: 'hover:bg-amber-600',
    testo: 'text-amber-700',
    tenue: 'bg-amber-50 text-amber-700',
    anello: 'ring-amber-400',
    esadecimale: '#f59e0b',
  },
  attendi_evento: {
    icona: Hourglass,
    pieno: 'bg-orange-600 text-white',
    pienoHover: 'hover:bg-orange-700',
    testo: 'text-orange-700',
    tenue: 'bg-orange-50 text-orange-700',
    anello: 'ring-orange-500',
    esadecimale: '#ea580c',
  },
  condizione: {
    icona: GitBranch,
    pieno: 'bg-violet-600 text-white',
    pienoHover: 'hover:bg-violet-700',
    testo: 'text-violet-700',
    tenue: 'bg-violet-50 text-violet-700',
    anello: 'ring-violet-500',
    esadecimale: '#7c3aed',
  },
  etichetta: {
    icona: Tag,
    pieno: 'bg-emerald-600 text-white',
    pienoHover: 'hover:bg-emerald-700',
    testo: 'text-emerald-700',
    tenue: 'bg-emerald-50 text-emerald-700',
    anello: 'ring-emerald-500',
    esadecimale: '#059669',
  },
  ingresso_evento: {
    icona: Zap,
    pieno: 'bg-fuchsia-600 text-white',
    pienoHover: 'hover:bg-fuchsia-700',
    testo: 'text-fuchsia-700',
    tenue: 'bg-fuchsia-50 text-fuchsia-700',
    anello: 'ring-fuchsia-500',
    esadecimale: '#c026d3',
  },
  fine: {
    icona: Flag,
    pieno: 'bg-slate-500 text-white',
    pienoHover: 'hover:bg-slate-600',
    testo: 'text-slate-600',
    tenue: 'bg-slate-100 text-slate-700',
    anello: 'ring-slate-400',
    esadecimale: '#64748b',
  },
};

/** I nomi corti che stanno sui tasti quadrati. Quelli lunghi restano nel pannello. */
export const NOME_CORTO: Record<StepKind, string> = {
  invio: 'Email',
  attesa: 'Aspetta',
  attendi_evento: 'Attendi',
  condizione: 'Domanda',
  etichetta: 'Lista',
  ingresso_evento: 'Ogni volta',
  fine: 'Fine',
};

/** L'ordine dei tasti per aggiungere: come si costruisce di solito un percorso. */
export const ORDINE_PASSI: StepKind[] = [
  'invio',
  'attesa',
  'attendi_evento',
  'condizione',
  'etichetta',
  'ingresso_evento',
  'fine',
];

/** Il formato con cui un tasto trascinato dice alla lavagna che casella porta. */
export const TIPO_TRASCINATO = 'application/x-slab-passo';

/** Vero se la stringa e' davvero un tipo di casella: arriva da un trascinamento. */
export function eTipoDiPasso(valore: string): valore is StepKind {
  return (ORDINE_PASSI as string[]).includes(valore);
}
